import { useUserContextPreferences } from '../../../../../hooks/useUserContextPreferences';
import { useGetCatalog } from '../../../../../services/modules/catalogs/catalogs.queries';
import { CatalogViewGroupBy } from '../view/catalogView.types';
import {
  CatalogGroupParams,
  ResolveGroupTargetIdParams,
  UseCatalogGroupParamsParams,
  UseCatalogGroupParamsResult,
} from './useCatalogGroupParams.types';

const NO_GROUP_PARAMS: CatalogGroupParams = {};

const resolveGroupTargetId = ({
  groupBy,
  preference,
  catalogTargetId,
}: ResolveGroupTargetIdParams): string | null => {
  if (groupBy !== CatalogViewGroupBy.CLASSIFICATION_TYPE) {
    return null;
  }

  if (preference?.groupBy) {
    return preference.groupTargetId ?? null;
  }

  return catalogTargetId;
};

export const useCatalogGroupParams = ({
  catalogId,
  canPersist,
}: UseCatalogGroupParamsParams): UseCatalogGroupParamsResult => {
  const catalogQuery = useGetCatalog({ id: catalogId }, { enabled: !!catalogId });
  const { getDefaultCatalogView, hasSettledUserPreferences } = useUserContextPreferences();

  const preference = canPersist ? getDefaultCatalogView() : null;

  const isSettled =
    (catalogQuery.isSuccess || catalogQuery.isError) && (!canPersist || hasSettledUserPreferences);

  const catalogGroupBy = catalogQuery.data?.viewGroupBy;
  const catalogTargetId = catalogQuery.data?.viewGroupTargetId ?? null;

  const groupBy = preference?.groupBy ?? catalogGroupBy;
  const targetId = resolveGroupTargetId({ groupBy, preference, catalogTargetId });

  const isSameAsCatalog = groupBy === catalogGroupBy && targetId === catalogTargetId;

  const isMissingTarget = groupBy === CatalogViewGroupBy.CLASSIFICATION_TYPE && !targetId;

  if (!isSettled || !groupBy || isSameAsCatalog || isMissingTarget) {
    return { groupParams: NO_GROUP_PARAMS, isSettled };
  }

  return {
    groupParams: { groupBy, groupTargetId: targetId ?? undefined },
    isSettled,
  };
};
